import {
  AutomationDefinition,
  jsonSchema,
} from "@redotech/redo-automations-model/graph/automation";
import {
  JsonValue,
  isJsonObject,
  stableStringify,
} from "@redotech/redo-automations-model/json";
import {
  CheckResult,
  ImpossibleBranch,
  checkAutomation,
} from "@redotech/redo-automations-model/lang/check";
import { MigrationRegistry } from "@redotech/redo-automations-model/lang/migrations";
import { KindRegistry } from "@redotech/redo-automations-model/registry/registry";
import * as z from "zod";
import { compileAutomation } from "../core/interpreter";
import { withStepOutputs } from "../core/state";
import { ExploreResult, explorePaths } from "../explore/explorer";
import {
  DEFAULT_START,
  Scenario,
  SimulatedEffects,
  simulate,
} from "../hosts/simulation";

/**
 * `cargo test` for automations: scenarios stored with the automation (as `test` blocks in
 * AutomationScript) and asserted in simulation. Agents that write an automation
 * write its tests too.
 */
const resolverFixture = z
  .object({
    value: jsonSchema.optional(),
    byArgs: z
      .array(
        z
          .object({ args: z.record(z.string(), jsonSchema), value: jsonSchema })
          .strict(),
      )
      .optional(),
    fail: z.boolean().optional(),
  })
  .strict()
  .describe(
    "{ value } answers every call; { byArgs: [{ args, value }] } answers by arguments; { fail: true } makes the lookup fail",
  );

const effectResult = z.union([
  z.object({ ok: z.literal(true), value: jsonSchema }).strict(),
  z
    .object({
      ok: z.literal(false),
      error: z
        .object({
          message: z.string(),
          code: z.string().optional(),
          retryable: z.boolean().optional(),
        })
        .strict(),
    })
    .strict(),
]);

/** What happened at one step: whether it ran, the port it took, when, and its inputs and output. */
const stepExpectation = z
  .object({
    ran: z.boolean().optional(),
    port: z.string().optional(),
    /** Seconds after the event at which the step ran. */
    at: z.number().optional(),
    /** Subset match against the step's evaluated inputs (recipients, targets, ...). */
    inputs: z.record(z.string(), jsonSchema).optional(),
    /** Subset match against the step's output. */
    output: jsonSchema.optional(),
    /** Subset match against what the step sent to the outside world (its effect's input, e.g. a function's input). */
    sent: jsonSchema.optional(),
  })
  .strict();

export const automationTestSchema = z
  .object({
    scenario: z
      .object({
        /** The event. Optional when `timeline` starts at 0 seconds. */
        payload: jsonSchema.optional(),
        startAt: z.string().optional(),
        seed: z.string().optional(),
        /** Records the automation reads and writes, by id; their fields answer lookups and steps' writes change them. */
        records: z
          .record(z.string(), z.record(z.string(), jsonSchema))
          .optional(),
        /** Answers for lookups by resolver name (see `check`/`scope` output), when `records` doesn't fit. */
        resolvers: z.record(z.string(), resolverFixture).optional(),
        events: z
          .array(
            z
              .object({
                eventType: z.string().optional(),
                nodeId: z.string().optional(),
                afterSeconds: z.number(),
                payload: jsonSchema,
              })
              .strict(),
          )
          .optional(),
        /** Results for effects by effect type, e.g. { "function.invoke": { ok: false, error: { message: "boom" } } }. */
        effects: z.record(z.string(), effectResult).optional(),
        effectsByNode: z.record(z.string(), effectResult).optional(),
        refreshes: z.array(jsonSchema).optional(),
        /** The trigger's live data after each wait (path explorer witnesses). */
        refreshByEpoch: z.array(jsonSchema).optional(),
        /** The trigger's live data over time: a refresh returns the latest entry at or before now. */
        timeline: z
          .array(
            z
              .object({ afterSeconds: z.number(), payload: jsonSchema })
              .strict(),
          )
          .optional(),
        /** The trigger's live entity is deleted this many seconds in; later refreshes keep the last values read. */
        goneAfterSeconds: z.number().min(0).optional(),
        /** Weighted branch draws by node id, from 0 (first variant) up to 1. */
        random: z.record(z.string(), z.number().min(0).max(1)).optional(),
      })
      .strict(),
    expect: z
      .object({
        status: z
          .enum(["running", "completed", "failed", "rejected", "cancelled"])
          .optional(),
        path: z.array(z.string()).optional(),
        ports: z.record(z.string(), z.string()).optional(),
        steps: z.record(z.string(), stepExpectation).optional(),
        /** Subset match against `scenario.records` after the run. */
        records: z
          .record(z.string(), z.record(z.string(), jsonSchema))
          .optional(),
        effects: z
          .array(
            z
              .object({
                type: z.string(),
                count: z.number().int().nonnegative(),
              })
              .strict(),
          )
          .optional(),
        /** Subset match against each node's recorded output. */
        outputs: z.record(z.string(), jsonSchema).optional(),
      })
      .strict(),
  })
  .strict();

export type AutomationTest = z.infer<typeof automationTestSchema>;

export interface AutomationTestResult {
  name: string;
  passed: boolean;
  failures: string[];
  /** The path the run took (nodeId:port), for proving explored paths. */
  taken?: { steps: string[]; status: string; rejection?: string };
}

function subset(expected: JsonValue, actual: JsonValue | undefined): boolean {
  if (isJsonObject(expected)) {
    return (
      isJsonObject(actual) &&
      Object.entries(expected).every(([k, v]) => subset(v, actual[k]))
    );
  }
  return stableStringify(expected) === stableStringify(actual ?? null);
}

export async function runAutomationTests(
  automation: AutomationDefinition,
  registry: KindRegistry,
  tests: { name: string; body: JsonValue }[],
  simulated: SimulatedEffects,
): Promise<AutomationTestResult[]> {
  const compiled = compileAutomation(automation, registry);
  const results: AutomationTestResult[] = [];
  for (const test of tests) {
    const parsed = automationTestSchema.safeParse(test.body);
    if (!parsed.success) {
      results.push({
        name: test.name,
        passed: false,
        failures: parsed.error.issues.map(
          (i) => `${i.path.join(".")}: ${i.message}`,
        ),
      });
      continue;
    }
    if (!compiled.ok) {
      results.push({
        name: test.name,
        passed: false,
        failures: compiled.diagnostics.map((d) => d.message),
      });
      continue;
    }
    const { scenario, expect } = parsed.data;
    const lookups = new Set<string>();
    for (const type of Object.values(compiled.compiled.types.ts.types)) {
      if (type.kind === "object") {
        for (const field of Object.values(type.fields)) {
          if (field.resolver) {
            lookups.add(field.resolver);
          }
        }
      }
    }
    const failures: string[] = scenarioProblems(
      automation,
      registry,
      scenario,
      expect,
      lookups,
    );
    if (failures.length > 0) {
      results.push({ name: test.name, passed: false, failures });
      continue;
    }
    const run = await simulate(
      compiled.compiled,
      toScenario(scenario),
      simulated,
    );
    const start = Date.parse(scenario.startAt ?? DEFAULT_START);
    for (const missing of run.unanswered) {
      failures.push(
        `Nothing answered ${missing.field} (lookup ${missing.resolver}), so it fell back to its default. Give it in scenario.records (by record id) or scenario.resolvers["${missing.resolver}"] = { "value": ... }`,
      );
    }
    for (const [nodeId, want] of Object.entries(expect.steps ?? {})) {
      const record = run.state.history.find((r) => r.nodeId === nodeId);
      const ran = record !== undefined && record.outcome !== "disabled";
      if (want.ran !== undefined && want.ran !== ran) {
        failures.push(
          `${nodeId}: expected it ${want.ran ? "to run" : "not to run"}`,
        );
      }
      if (!record) {
        if (want.ran !== false) {
          failures.push(`${nodeId}: didn't run`);
        }
        continue;
      }
      if (want.port !== undefined && record.port !== want.port) {
        failures.push(
          `${nodeId}: expected port ${want.port}, got ${record.port ?? "(none)"}`,
        );
      }
      const at = (Date.parse(record.startedAt) - start) / 1000;
      if (want.at !== undefined && at !== want.at) {
        failures.push(`${nodeId}: expected it at ${want.at}s, ran at ${at}s`);
      }
      if (want.inputs && !subset(want.inputs, record.inputs ?? {})) {
        failures.push(
          `${nodeId} inputs: expected ${stableStringify(want.inputs)}, got ${stableStringify(record.inputs ?? {})}`,
        );
      }
      const output = withStepOutputs(
        run.state.history,
        run.state.context,
      ).findLast((r) => r.nodeId === nodeId)?.output;
      if (want.output !== undefined && !subset(want.output, output)) {
        failures.push(
          `${nodeId} output: expected ${stableStringify(want.output)}, got ${stableStringify(output ?? null)}`,
        );
      }
      if (want.sent !== undefined) {
        const sent = record.effects
          .filter((e) => e.type !== "context.resolve")
          .map((e) => e.input);
        if (!sent.some((input) => subset(want.sent ?? null, input))) {
          failures.push(
            `${nodeId} sent: expected ${stableStringify(want.sent)}, got ${stableStringify(sent)}`,
          );
        }
      }
    }
    for (const [id, want] of Object.entries(expect.records ?? {})) {
      const actual = run.records[id];
      if (!subset(want, actual)) {
        failures.push(
          `record ${id}: expected ${stableStringify(want)}, got ${stableStringify(actual ?? null)}`,
        );
      }
    }
    if (expect.status && run.state.status !== expect.status) {
      failures.push(
        `status: expected ${expect.status}, got ${run.state.status}`,
      );
    }
    if (
      expect.path &&
      stableStringify(expect.path) !== stableStringify(run.path)
    ) {
      failures.push(
        `path: expected ${expect.path.join(" → ") || "∅"}, got ${run.path.join(" → ") || "∅"}`,
      );
    }
    for (const [nodeId, port] of Object.entries(expect.ports ?? {})) {
      const record = run.state.history.find((r) => r.nodeId === nodeId);
      if (record?.port !== port) {
        failures.push(
          `${nodeId}: expected port ${port}, got ${record?.port ?? "(didn't run)"}`,
        );
      }
    }
    for (const { type, count } of expect.effects ?? []) {
      const actual = [
        ...run.state.admission,
        ...run.state.history.flatMap((r) => r.effects),
      ].filter((e) => e.type === type).length;
      if (actual !== count) {
        failures.push(`effect ${type}: expected ${count}, got ${actual}`);
      }
    }
    for (const [nodeId, output] of Object.entries(expect.outputs ?? {})) {
      const record = run.state.history.find((r) => r.nodeId === nodeId);
      if (!subset(output, record?.output)) {
        failures.push(
          `${nodeId} output: expected ${stableStringify(output)}, got ${stableStringify(record?.output ?? null)}`,
        );
      }
    }
    results.push({
      name: test.name,
      passed: failures.length === 0,
      failures,
      taken: {
        steps: run.state.history.map((r) => `${r.nodeId}:${r.port ?? ""}`),
        status: run.state.status,
        ...(run.state.rejection ? { rejection: run.state.rejection } : {}),
      },
    });
  }
  return results;
}

/** References in a test that can't be right: unknown node ids, draws for steps that don't draw. */
function scenarioProblems(
  automation: AutomationDefinition,
  registry: KindRegistry,
  scenario: AutomationTest["scenario"],
  expect: AutomationTest["expect"],
  lookups: ReadonlySet<string>,
): string[] {
  const problems: string[] = [];
  const effectTypes = [...registry.effects.keys()];
  for (const type of [
    ...Object.keys(scenario.effects ?? {}),
    ...(expect.effects ?? []).map((e) => e.type),
  ]) {
    if (!registry.effects.has(type)) {
      problems.push(
        `effect "${type}" doesn't exist (effect types: ${effectTypes.join(", ")})`,
      );
    }
  }
  const ids = new Set(automation.nodes.map((n) => n.id));
  const known = (where: string, keys: string[]) => {
    for (const key of keys) {
      if (!ids.has(key)) {
        problems.push(
          `${where}.${key}: no step has this id (steps: ${[...ids].join(", ")})`,
        );
      }
    }
  };
  known("scenario.random", Object.keys(scenario.random ?? {}));
  for (const node of automation.nodes) {
    const kind = registry.steps.get(node.kind);
    if (
      !Object.hasOwn(scenario.random ?? {}, node.id) ||
      !kind?.parse(node.config).ok
    ) {
      continue;
    }
    const explore = kind.explore(node.config);
    const draws =
      explore?.type === "outcomes" &&
      explore.outcomes.some((o) => o.weight !== undefined);
    if (!draws) {
      problems.push(
        `scenario.random.${node.id}: this step doesn't make a random draw (only weighted branches do)`,
      );
    }
  }
  known("scenario.effectsByNode", Object.keys(scenario.effectsByNode ?? {}));
  known(
    "scenario.events",
    (scenario.events ?? []).flatMap((e) => (e.nodeId ? [e.nodeId] : [])),
  );
  for (const resolver of Object.keys(scenario.resolvers ?? {})) {
    if (!lookups.has(resolver)) {
      problems.push(
        `scenario.resolvers.${resolver}: no field in this automation is looked up by "${resolver}" (lookups: ${[...lookups].join(", ") || "none"})`,
      );
    }
  }
  known("expect.steps", Object.keys(expect.steps ?? {}));
  known("expect.ports", Object.keys(expect.ports ?? {}));
  known("expect.outputs", Object.keys(expect.outputs ?? {}));
  if (
    scenario.payload === undefined &&
    !(scenario.timeline ?? []).some((e) => e.afterSeconds === 0)
  ) {
    problems.push(
      "scenario.payload: give the event, or a timeline entry at afterSeconds 0",
    );
  }
  return problems;
}

/** A scenario's event: its payload, or the timeline's entry at 0 seconds. */
export function scenarioPayload(
  s: AutomationTest["scenario"],
): JsonValue | undefined {
  return (
    s.payload ?? (s.timeline ?? []).find((e) => e.afterSeconds === 0)?.payload
  );
}

/** A test block's scenario as simulation takes it. */
export function toScenario(s: AutomationTest["scenario"]): Scenario {
  return {
    payload: scenarioPayload(s) ?? null,
    ...(s.records ? { records: s.records } : {}),
    ...(s.startAt ? { startAt: s.startAt } : {}),
    ...(s.seed ? { seed: s.seed } : {}),
    ...(s.resolvers ? { resolvers: s.resolvers } : {}),
    ...(s.events ? { events: s.events } : {}),
    ...(s.effects ? { effects: s.effects } : {}),
    ...(s.effectsByNode ? { effectsByNode: s.effectsByNode } : {}),
    ...(s.refreshes ? { refreshes: s.refreshes } : {}),
    ...(s.refreshByEpoch ? { refreshByEpoch: s.refreshByEpoch } : {}),
    ...(s.timeline ? { timeline: s.timeline } : {}),
    ...(s.goneAfterSeconds !== undefined
      ? { goneAfterSeconds: s.goneAfterSeconds }
      : {}),
    ...(s.random ? { random: s.random } : {}),
  };
}

/** Branches only infeasible paths take: no input can drive them (L0006). */
export function impossibleBranches(explore: ExploreResult): ImpossibleBranch[] {
  if (explore.truncated) {
    return [];
  }
  const reachable = new Set(
    explore.paths
      .filter((p) => p.status !== "infeasible")
      .flatMap((p) => p.steps.map((s) => `${s.nodeId}:${s.port}`)),
  );
  const out = new Map<string, ImpossibleBranch>();
  for (const path of explore.paths.filter((p) => p.status === "infeasible")) {
    const step = path.steps.find(
      (s) => s.how === "branch" && !reachable.has(`${s.nodeId}:${s.port}`),
    );
    if (step) {
      out.set(`${step.nodeId}:${step.port}`, {
        nodeId: step.nodeId,
        port: step.port,
      });
    }
  }
  return [...out.values()];
}

export interface Verification {
  ok: boolean;
  check: CheckResult;
  explore: ExploreResult | null;
  tests: AutomationTestResult[];
  /** Why the gate failed beyond check errors and failing tests (unproven paths, no tests). */
  problems: string[];
  /** What the gate couldn't check, without failing it (paths past the exploration cap). */
  notes: string[];
}

/**
 * The publish gate: no check errors, every explored path proven or
 * impossible, and every stored test passing. Paths past the exploration cap
 * aren't explored; that is a note, not a failure.
 */
export async function verifyAutomation(
  automation: AutomationDefinition,
  registry: KindRegistry,
  options: {
    tests?: { name: string; body: JsonValue }[];
    simulated: SimulatedEffects;
    migrations?: MigrationRegistry;
    /** Publishing needs at least one stored test (agents and the CLI set this). */
    requireTests?: boolean;
  },
): Promise<Verification> {
  const first = checkAutomation(automation, registry, {
    ...(options.migrations ? { migrations: options.migrations } : {}),
  });
  if (!first.ok) {
    return {
      ok: false,
      check: first,
      explore: null,
      tests: [],
      problems: ["Fix the check errors first"],
      notes: [],
    };
  }
  const compiled = compileAutomation(automation, registry);
  const tests = await runAutomationTests(
    automation,
    registry,
    options.tests ?? [],
    options.simulated,
  );
  const explored = compiled.ok
    ? await explorePaths(compiled.compiled, options.simulated)
    : null;
  const explore = explored ? provenByTests(explored, tests) : null;
  const check = checkAutomation(automation, registry, {
    ...(options.migrations ? { migrations: options.migrations } : {}),
    impossible: explore ? impossibleBranches(explore) : [],
  });
  const problems: string[] = [];
  for (const path of explore?.paths.filter((p) => p.status === "unknown") ??
    []) {
    problems.push(
      `Unproven path ${path.steps.map((s) => `${s.nodeId}:${s.port}`).join(" → ") || "(start)"} (${path.end}): ${path.note ?? "no witness"}. Add a test that takes it.`,
    );
  }
  const notes: string[] = [];
  if (explore?.truncated) {
    notes.push(
      `Only the first ${explore.paths.length} paths were explored; steps that appear only on later paths weren't checked`,
    );
  }
  if (options.requireTests && (options.tests ?? []).length === 0) {
    problems.push(
      "No tests: add test blocks covering what the automation should and shouldn't do",
    );
  }
  return {
    ok:
      check.ok &&
      explore !== null &&
      problems.length === 0 &&
      tests.every((t) => t.passed),
    check,
    explore,
    tests,
    problems,
    notes,
  };
}

/** A passing test that takes a path the explorer couldn't prove is that path's witness. */
function provenByTests(
  explore: ExploreResult,
  tests: AutomationTestResult[],
): ExploreResult {
  const paths = explore.paths.map((path) => {
    if (path.status !== "unknown") {
      return path;
    }
    const steps = path.steps.map((s) => `${s.nodeId}:${s.port}`);
    const test = tests.find(
      (t) =>
        t.passed &&
        t.taken &&
        stableStringify(t.taken.steps) === stableStringify(steps) &&
        (path.end === "completed"
          ? t.taken.status === "completed"
          : t.taken.status === "rejected" &&
            path.end === `rejected:${t.taken.rejection ?? ""}`),
    );
    return test
      ? {
          ...path,
          status: "proven" as const,
          note: `Proven by test "${test.name}"`,
        }
      : path;
  });
  const reached = new Set(
    paths
      .filter((p) => p.status !== "infeasible")
      .flatMap((p) => p.steps.map((s) => s.nodeId)),
  );
  return {
    ...explore,
    paths,
    unreachable: explore.unreachable.filter((id) => !reached.has(id)),
  };
}
